import React, { useEffect, useState } from "react";
import { useDashboard } from "../context/DashboardContext";
import api from "../lib/api";
import { PageHeader, StatCard } from "../components/common/PageParts";
import { fmtPrice } from "../lib/format";
import { Beaker, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function PaperTrading() {
  const { pairs, activeSymbol } = useDashboard();
  const [positions, setPositions] = useState([]);
  const [form, setForm] = useState({ symbol: activeSymbol, side: "LONG", size: 0.1 });

  const load = () => api.getPositions().then(setPositions).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  useEffect(() => { setForm((f) => ({ ...f, symbol: activeSymbol })); }, [activeSymbol]);

  const open = async () => {
    try {
      const t = await api.getTicker(form.symbol);
      await api.openPosition({ symbol: form.symbol, side: form.side, size: Number(form.size), entry: t.price });
      toast.success(`Opened ${form.side} ${form.symbol} @ ${fmtPrice(t.price)}`);
      load();
    } catch { toast.error("Could not open position"); }
  };
  const close = async (id) => { const r = await api.closePosition(id); toast.success(`Closed · PnL $${r.pnl}`); load(); };

  const openPnl = positions.reduce((a, p) => a + (p.pnl || 0), 0);

  return (
    <div className="ym-fade">
      <PageHeader title="Paper Trading" subtitle="Practice with live prices — no real funds"
        right={<span className="pill" style={{ color: "var(--accent-soft)", background: "rgba(139,92,246,.12)", borderColor: "transparent" }}><Beaker size={13} /> Simulated</span>} />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <StatCard label="Open Positions" value={positions.length} />
        <StatCard label="Unrealised PnL" value={`$${openPnl.toFixed(2)}`} color={openPnl >= 0 ? "var(--green)" : "var(--red)"} />
        <StatCard label="Market Data" value="LIVE" color="var(--green)" sub="Prices from providers" />
      </div>

      <div className="ym-panel p-4 mb-4">
        <h3 className="text-[13px] font-bold mb-3">New Paper Order</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px]" style={{ color: "var(--text-muted)" }}>Pair</label>
            <select value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })}
              className="block mt-1 rounded-lg px-3 py-2 text-[13px] outline-none" style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }}>
              {pairs.map((p) => <option key={p.id} value={p.symbol}>{p.symbol}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px]" style={{ color: "var(--text-muted)" }}>Side</label>
            <div className="flex gap-1 mt-1">
              {["LONG", "SHORT"].map((s) => (
                <button key={s} onClick={() => setForm({ ...form, side: s })} className="px-4 py-2 rounded-lg text-[12px] font-bold ym-hover"
                  style={{ background: form.side === s ? (s === "LONG" ? "rgba(34,197,94,.18)" : "rgba(239,68,68,.18)") : "var(--panel-2)", color: form.side === s ? (s === "LONG" ? "var(--green)" : "var(--red)") : "var(--text-muted)", border: "1px solid var(--border)" }}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px]" style={{ color: "var(--text-muted)" }}>Size</label>
            <input type="number" step="0.01" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}
              className="block mt-1 w-28 rounded-lg px-3 py-2 text-[13px] tabnum outline-none" style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />
          </div>
          <button onClick={open} className="flex items-center gap-1.5 rounded-lg px-5 py-2 text-[13px] font-semibold ym-hover" style={{ background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff" }}>
            <Plus size={15} /> Open
          </button>
        </div>
      </div>

      <div className="ym-panel p-4">
        <h3 className="text-[13px] font-bold mb-3">Open Positions</h3>
        {!positions.length && <div className="text-center py-6 text-[12px]" style={{ color: "var(--text-dim)" }}>No open positions.</div>}
        <div className="space-y-2">
          {positions.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-lg px-3 py-2.5" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
              <span className="font-bold text-[13px]">{p.symbol}</span>
              <span className="pill" style={{ color: p.side === "LONG" ? "var(--green)" : "var(--red)", background: "transparent", borderColor: "var(--border)" }}>{p.side}</span>
              <span className="text-[12px] tabnum" style={{ color: "var(--text-muted)" }}>{p.size} @ ${fmtPrice(p.entry)}</span>
              <span className="text-[12px] tabnum" style={{ color: "var(--text-muted)" }}>now ${fmtPrice(p.current)}</span>
              <span className="ml-auto tabnum font-bold text-[13px]" style={{ color: (p.pnl || 0) >= 0 ? "var(--green)" : "var(--red)" }}>${(p.pnl || 0).toFixed(2)} ({(p.pnlPct || 0).toFixed(2)}%)</span>
              <button onClick={() => close(p.id)} className="grid place-items-center rounded-md ym-hover" style={{ width: 26, height: 26, background: "var(--panel)", border: "1px solid var(--border)" }}><X size={13} style={{ color: "var(--red)" }} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
